import {
  useCurrentDate,
  Button,
  createSellarColumnHelper,
} from "@sellar-io/components";
import { BuyerRelationshipListResponseType } from "@sellar-io/api-v2/types";
import {
  getNextCourierDispatchAndDeliveryDates,
  getNextStartDate,
} from "@sellar-io/common";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";

import dayjs from "dayjs";
import { createRef, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useShallow } from "zustand/react/shallow";
import { Icon, ProfilePicture } from "../../../../components";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../../../components/dropdown/Dropdown";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../components/modal/Dialog";
import { usePaymentTermsIndicator } from "../../../../components/paymentTypeIndicator/usePaymentTermsIndicator";
import {
  BuyerRelationshipsSortType,
  useBuyerRelationshipsStore,
} from "../buyerRelationshipsStore";
import { DIRECT_MESSAGE, ORDER_BUYER } from "../../../../constants/routes";
import { useFeatureFlagStore } from "../../../../hooks/useFeatureFlags";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { formatDate } from "../../../../utils/formatDate";
import { getNextDirectDeliveryDate } from "../../../order/helpers/DeliveryDateHelpers/getNextDeliveryDate";
import OrderFulfillmentIndicator from "../../../order/orderFulfillmentIndicator/OrderFulfillmentIndicator";
import { CreateConnectionPaymentTermsRequestDialog } from "../Dialogs/CreateConnectionPaymentTermsRequestDialog";
import { RemoveFromListDialog } from "../Dialogs/RemoveFromListDialog";
import { useSupplierListStore } from "./SupplierListStore";
import { Badge, BadgeLime } from "../../../../components/badge/Badge";
import { TooltipContent } from "@sellar-io/components";
import { TooltipTrigger } from "@sellar-io/components";
import { Tooltip } from "@sellar-io/components";
import { useAllOtherPaymentOptions } from "../../../../components/paymentTypeIndicator/useAllOtherPaymentOptions";
import { TableInfinite } from "../../../../components/table/TableInfinite";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useNavigateToChild } from "../../../../hooks/useParentNavigation";
import { useBuyerRelationshipDeliveryEstimate } from "../BuyerRelationshipDeliveryEstimate/useBuyerRelationshipDeliveryEstimate";
import { useCreditTermsText } from "../../../../components/paymentTypeIndicator/useCreditTermsText";

dayjs.extend(utc);
dayjs.extend(timezone);

export const BuyerRelationshipsTable = ({
  buyerRelationshipListQuery,
  connectionCount,
}: {
  buyerRelationshipListQuery: UseInfiniteQueryResult<
    InfiniteData<BuyerRelationshipListResponseType>
  >;
  connectionCount: number | undefined;
}) => {
  const navigateToChild = useNavigateToChild();
  const { business } = useSellarCurrentBusiness();
  if (!business?.Retailer?.id) throw new Error("No buyer found");
  const { search, isFavourite } = useSupplierListStore(
    useShallow((state) => ({
      search: state.search,
      isFavourite: state.isFavourite,
    })),
  );

  const { data, fetchNextPage, isFetching } = buyerRelationshipListQuery;

  const { isActive } = useRemoteFeatureFlags();
  const isImplicitProformaActive = isActive("implicitProforma");

  const flatData = useMemo(
    () => data?.pages.flatMap((page) => page) ?? [],
    [data],
  );

  const { columnSortName, setColumnSortName } = useBuyerRelationshipsStore(
    useShallow((state) => ({
      columnSortName: state.columnSortName,
      setColumnSortName: state.setColumnSortName,
    })),
  );

  const handleTableSort = (columnSort: BuyerRelationshipsSortType | null) => {
    setColumnSortName(columnSort ?? "name_asc");
  };

  const columnHelper =
    createSellarColumnHelper<BuyerRelationshipListResponseType[number]>();

  const allColumns = [
    columnHelper.accessor((row) => row, {
      id: "name",
      header: "Supplier",
      size: 400,
      cell: (info) => {
        return (
          <SupplierNameCell
            buyerRelationship={info.getValue()}
            showCreditTerms={isImplicitProformaActive}
            onClick={(supplierUrlId) =>
              navigateToChild(
                ORDER_BUYER.href({ supplierIdOrUrlId: supplierUrlId }),
              )
            }
          />
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "paymentTerms",
      size: 350,
      enableSorting: false,
      header: () => {
        return <PaymentOptionsHeader />;
      },
      cell: (info) => {
        const buyerRelationship = info.getValue();
        return <PaymentTermsColumnCell buyerRelationship={buyerRelationship} />;
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "delivery",
      header: "Delivery",
      size: 350,
      enableSorting: false,
      cell: (info) => {
        return (
          <BuyerRelationshipDelivery buyerRelationship={info.getValue()} />
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "last_ordered",
      header: "Last ordered",
      size: 200,
      cell: (info) => {
        const buyerRelationship = info.getValue();
        const terms = buyerRelationship.Relationship.BuyerRelationshipDetails;
        return terms?.Analytics?.lastOrderDate
          ? dayjs(terms.Analytics.lastOrderDate).format("DD/MM/YYYY")
          : "Not ordered yet";
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "menu",
      header: "",
      size: 10,
      enableSorting: false,
      cell: (info) => {
        const buyerRelationshipId = info.getValue().id;
        const relationshipId = info.getValue().RelationshipId;
        return (
          <BuyerRelationshipTableMenuDropdown
            buyerRelationshipId={buyerRelationshipId}
            relationshipId={relationshipId}
          />
        );
      },
    }),
  ];

  const columns = isImplicitProformaActive
    ? allColumns.filter((column) => column.id !== "paymentTerms")
    : allColumns;

  const gridClasses = isImplicitProformaActive
    ? "grid-cols-[minmax(400px,_1fr)_1fr_1fr_70px]"
    : "grid-cols-[minmax(400px,_1fr)_minmax(150px,_1fr)_1fr_1fr_70px]";

  return (
    <TableInfinite
      columns={columns}
      data={flatData}
      infiniteQuery={{
        data: flatData,
        fetchNextPage,
        isFetching,
        totalCount: connectionCount,
      }}
      columnSortName={columnSortName}
      handleTableSort={handleTableSort}
      noResultsText={
        search || isFavourite
          ? "No results found in your supplier list"
          : "Suppliers that you connect with on Sellar will appear here"
      }
      gridClasses={gridClasses}
    />
  );
};

const SupplierNameCell = ({
  buyerRelationship,
  showCreditTerms,
  onClick,
}: {
  buyerRelationship: BuyerRelationshipListResponseType[number];
  showCreditTerms: boolean;
  onClick: (supplierUrlId: string) => void;
}) => {
  const relationship = buyerRelationship.Relationship;
  const supplier = relationship.Supplier;
  const business = supplier.Business;
  const connection = relationship.BuyerRelationshipDetails;
  const isFavourited = !!buyerRelationship.BuyerRelationshipFavourite;
  const creditTermsText = useCreditTermsText({ connection });
  const hasSupplierCredit =
    showCreditTerms &&
    connection?.approved &&
    connection.paymentTerms === "credit";

  return (
    <div
      className="group flex cursor-pointer items-center"
      onClick={() => onClick(supplier.urlId)}
    >
      <figure className="relative">
        <ProfilePicture
          size="small"
          name={business.name}
          logoUrl={business.logoUrl}
          className="mr-2 h-[28px] max-h-[28px] min-h-[28px] w-[28px] max-w-[28px] min-w-[28px] text-[8px]"
        />
      </figure>
      <span className="group-hover:underline">{business.name} </span>
      <div className="ml-2 flex items-center gap-1">
        {isFavourited && (
          <div className="relative flex h-6 w-6 items-center justify-center rounded-xl bg-purple-100 outline outline-1 outline-offset-[-1px] outline-purple-100">
            <Icon className="text-purple-800" size={0.7} icon="HEART" />
          </div>
        )}
        {hasSupplierCredit && (
          <Tooltip>
            <TooltipTrigger className="flex w-fit">
              <BadgeLime
                icon="CALENDAR_CLOCK_OUTLINE"
                aria-label="Supplier credit"
              />
            </TooltipTrigger>
            <TooltipContent
              className={
                "Tooltip flex justify-center rounded-lg bg-zinc-800 px-2 py-1 text-sm text-white"
              }
            >
              {creditTermsText}
              <span className="absolute -bottom-1 block h-2 w-2 rotate-45 bg-zinc-800" />
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const PaymentTermsColumnCell = ({
  buyerRelationship,
}: {
  buyerRelationship: BuyerRelationshipListResponseType[number];
}) => {
  const { isActive } = useFeatureFlagStore();
  const [active, setActive] = useState<boolean>(false);
  const connection = buyerRelationship.Relationship.BuyerRelationshipDetails;
  const supplier = buyerRelationship.Relationship.Supplier;
  const paymentTermsRequest =
    buyerRelationship.Relationship.BuyerRelationshipDetails
      ?.PaymentTermsRequest;
  const { full, type, textColor } = usePaymentTermsIndicator({
    connection: connection
      ? {
          ...connection,
          paymentDue: connection.paymentDue ?? null,
          paymentDueCalculateFrom: connection.paymentDueCalculateFrom ?? null,
          paymentTerms: connection.paymentTerms ?? null,
        }
      : null,
    invoiceCreationStage: supplier.invoiceCreationStage,
    approved: connection?.approved ?? false,
    paymentTermsRequest: connection?.PaymentTermsRequest,
    hasDefaultPriceTier: !!supplier.CustomerZones[0],
    supplier,
  });

  const allOtherPaymentOptions = useAllOtherPaymentOptions({ connection });

  const hasTermsAssigned =
    type === "credit" || type === "proForma" || type === "paymentOnDelivery";
  const isRequestedTermsType = type === "termsRequested";
  const isOnlyBuyerList = type === "default";
  const canEdit =
    !paymentTermsRequest &&
    !isRequestedTermsType &&
    !isOnlyBuyerList &&
    isActive("CHECKOUT_CREDIT_TERMS_REQUEST");

  const toolTipRef = createRef<HTMLDivElement>();

  return (
    <>
      <div
        className={twMerge(
          "flex w-full flex-row items-center gap-2",
          canEdit && "cursor-pointer",
        )}
        onClick={() => (canEdit ? setActive(true) : undefined)}
      >
        {full}
        {type === "sellarCredit" && allOtherPaymentOptions && (
          <Tooltip>
            <TooltipTrigger className="w-fit">
              <Badge
                className={
                  "pointer-events-auto cursor-pointer items-center gap-1 border bg-zinc-50 text-zinc-800"
                }
                ref={toolTipRef}
              >
                <span className="text-sm text-zinc-800">
                  +{allOtherPaymentOptions.length}
                </span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent
              className={
                "Tooltip flex justify-center rounded-lg bg-zinc-800 px-2 py-1 text-sm text-white"
              }
            >
              {allOtherPaymentOptions
                .map((option) => option.pretty)
                .toString()
                .split(",")
                .join(", ")}
              <span className="absolute -bottom-1 block h-2 w-2 rotate-45 bg-zinc-800" />
            </TooltipContent>
          </Tooltip>
        )}
        {canEdit && hasTermsAssigned && (
          <Icon
            size={0.5}
            className={twMerge("cursor-pointer", textColor && textColor)}
            icon={"PENCIL"}
          />
        )}
      </div>
      {connection && (
        <CreateConnectionPaymentTermsRequestDialog
          active={active}
          onDismiss={() => setActive(false)}
          connectionId={connection.id}
          supplierName={supplier.Business.name}
        />
      )}
    </>
  );
};

const BuyerRelationshipTableMenuDropdown = ({
  buyerRelationshipId,
  relationshipId,
}: {
  buyerRelationshipId: number;
  relationshipId: number;
}) => {
  const navigateToChild = useNavigateToChild();
  const [active, setActive] = useState<boolean>(false);
  const onDismiss = () => setActive(false);
  return (
    <>
      <Dropdown>
        <DropdownButton>
          <Button size={"small"} color={"transparent"}>
            <Icon icon={"DOTS_VERTICAL"} className="text-black" />
          </Button>
        </DropdownButton>
        <DropdownItems>
          <DropdownItem
            className="border-b border-zinc-200"
            onClick={() =>
              navigateToChild(DIRECT_MESSAGE.href({ relationshipId }))
            }
          >
            Message
          </DropdownItem>
          <DropdownItem
            className="border-b border-zinc-200 text-red-700"
            onClick={() => setActive(true)}
          >
            Remove from list
          </DropdownItem>
        </DropdownItems>
      </Dropdown>
      <RemoveFromListDialog
        active={active}
        onDismiss={onDismiss}
        buyerRelationshipId={buyerRelationshipId}
      />
    </>
  );
};

const PaymentOptionsHeader = () => {
  const { isActive } = useFeatureFlagStore();
  const creditTermsFeatureFlagActive = isActive(
    "CHECKOUT_CREDIT_TERMS_REQUEST",
  );
  const [active, setActive] = useState<boolean>(false);
  const onDismiss = () => setActive(false);
  return (
    <>
      <div className={"inline-flex items-center gap-1"}>
        <p>Payment options</p>
        {creditTermsFeatureFlagActive && (
          <Icon
            icon={"INFORMATION_OUTLINE"}
            className={"cursor-pointer"}
            size={0.7}
            onClick={() => setActive(true)}
          />
        )}
      </div>
      <Dialog active={active} onDismiss={onDismiss}>
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={onDismiss}
          title={"Payment information"}
        />
        <DialogContent>
          <p>
            The payment options available to you between you and your supplier
            are listed. You may request a change to your agreed terms with your
            supplier if you have not done so already, and you have ordered from
            them on Sellar before.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

const BuyerRelationshipDelivery = ({
  buyerRelationship,
}: {
  buyerRelationship: BuyerRelationshipListResponseType[number];
}) => {
  const currentDate = useCurrentDate();
  const terms = buyerRelationship.Relationship.BuyerRelationshipDetails;
  const supplier = buyerRelationship.Relationship.Supplier;
  const isCourier =
    terms?.approved &&
    terms.fulfillmentType === "courier" &&
    !terms.courierDeliveryMethodHiddenAt;
  const earliestSupplierOrderDispatchDate = getNextStartDate({
    latestAvailablePreOrderFromDate: null,
    outOfOfficeReturnDate: supplier.SupplierOutOfOffice?.outOfOffice
      ? supplier.SupplierOutOfOffice.returnDate
      : null,
    courierDeliveryScheduleStartDate: null,
    currentDate,
  });

  // calculate direct delivery estimates

  let nextDirectDeliveryDates =
    supplier.DeliveryArea && !terms?.drayDeliveryMethodHiddenAt
      ? getNextDirectDeliveryDate({
          deliveryArea: supplier.DeliveryArea,
          startDateReference: earliestSupplierOrderDispatchDate,
        })
      : null;

  // calculate SD delivery estimates

  const supplierId = buyerRelationship.Relationship.SupplierId;

  const { getDistributionDeliveryEstimate } =
    useBuyerRelationshipDeliveryEstimate();

  const sdDeliveryEstimate = getDistributionDeliveryEstimate(supplierId);

  const sdNextDeliveryDate = sdDeliveryEstimate?.deliveryDate ?? null;

  // calculate courier estimates

  const courierDeliverySchedule = supplier.CourierDeliverySchedule;
  const courierDeliveryScheduleDays =
    !!courierDeliverySchedule?.CourierDeliveryScheduleDay.length;
  const showDeliverySchedule =
    !!courierDeliverySchedule && courierDeliveryScheduleDays;
  const courierDeliveryScheduleEstimations = showDeliverySchedule
    ? getNextCourierDispatchAndDeliveryDates({
        orderCutOffTime: dayjs(courierDeliverySchedule.orderCutOffTime)
          .utc()
          .format("HH:mm"),
        courierDaysSchedule: courierDeliverySchedule.CourierDeliveryScheduleDay,
        dispatchDaysAfterConfirmation:
          courierDeliverySchedule.dispatchDaysAfterConfirmation,
        deliveryDaysAfterDispatch:
          courierDeliverySchedule.deliveryDaysAfterDispatch,
        startDateReference: currentDate,
        allowSaturdayDelivery: courierDeliverySchedule.allowSaturdayDelivery,
      })
    : null;

  const estimatedCourierDelivery =
    courierDeliveryScheduleEstimations?.estimatedDeliveryDate.toDate();

  const deliveryDate = isCourier
    ? estimatedCourierDelivery
    : nextDirectDeliveryDates?.deliveryDate;

  // get fulfillment type

  const defaultFulfillmentType = nextDirectDeliveryDates?.deliveryDate
    ? sdDeliveryEstimate?.isSellarDistributionEnabled
      ? "sellarDistribution"
      : "direct"
    : "courier";

  const termsFulfillmentType = () => {
    if (!terms) return "courier";

    if (terms.fulfillmentType === "sellarDistribution") {
      return "sellarDistribution";
    }

    if (!terms.drayDeliveryMethodHiddenAt && terms.fulfillmentType === "direct")
      return "direct";
    if (
      !terms.courierDeliveryMethodHiddenAt &&
      ["courier", "direct"].includes(terms.fulfillmentType)
    )
      return "courier";
    if (
      !terms.palletDeliveryMethodHiddenAt &&
      ["courier", "direct", "haulier"].includes(terms.fulfillmentType)
    )
      return "haulier";

    return "courier";
  };

  const fulfillmentType = terms?.approved
    ? termsFulfillmentType()
    : defaultFulfillmentType;

  if (fulfillmentType === "sellarDistribution") {
    return (
      <div className={"inline-flex flex-wrap items-center gap-1"}>
        <Badge
          icon="SELLAR_DISTRIBUTION_TRUCK"
          iconViewBox="0 0 16 16"
          className="bg-gray-100 text-[#8C23C7]"
        >
          <span className="text-zinc-800">
            Delivered by<span className="text-[#8C23C7]"> Sellar</span>
          </span>
        </Badge>
        {sdNextDeliveryDate && (
          <p>
            Delivery by: <strong>{formatDate(sdNextDeliveryDate)}</strong>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={"inline-flex flex-wrap items-center gap-1"}>
      <OrderFulfillmentIndicator fulfillmentType={fulfillmentType} />
      {deliveryDate && (
        <p>
          Delivery by: <strong>{formatDate(deliveryDate)}</strong>
        </p>
      )}
    </div>
  );
};
